import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { ShieldCheck, BadgeCheck, XCircle, FileText, ExternalLink } from "lucide-react";
import { api } from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyMember() {
  const { memberId } = useParams();
  const [params] = useSearchParams();
  const certNo = params.get("cert");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/verify/${memberId}${certNo ? `?cert=${certNo}` : ""}`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e?.response?.data?.detail || "Invalid"));
  }, [memberId, certNo]);

  if (error) {
    return (
      <div className="fade-in min-h-[70vh] flex items-center justify-center px-4">
        <div className="max-w-md bg-white rounded-3xl border border-[#eae5d9] p-10 text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" strokeWidth={1.4} />
          <h1 className="serif text-2xl text-[#000080] font-semibold">Verification Failed</h1>
          <p className="text-sm text-[#737373] mt-2">No member exists with ID <b>{memberId}</b>.</p>
          <p className="text-xs text-[#737373] mt-4">If you believe this is an error, contact us at Support@adopthelplesschildren.com</p>
        </div>
      </div>
    );
  }

  if (!data) return <div className="min-h-[60vh] flex items-center justify-center text-[#737373]">Verifying…</div>;

  return (
    <div className="fade-in max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-16">
      {/* Tricolour strip */}
      <div className="flex h-1.5 rounded-full overflow-hidden mb-6">
        <div className="flex-1 bg-[#FF9933]" />
        <div className="flex-1 bg-white border-y border-[#eae5d9]" />
        <div className="flex-1 bg-[#138808]" />
      </div>

      <div className="bg-white rounded-3xl border-2 border-[#138808] p-8 md:p-10 shadow-lg" data-testid="verify-card">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-full bg-[#138808]/15 flex items-center justify-center">
            <BadgeCheck className="w-7 h-7 text-[#138808]" strokeWidth={1.7} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#138808] font-bold">Verified Member</div>
            <div className="serif text-2xl text-[#000080] font-semibold leading-none">{data.organization}</div>
          </div>
        </div>

        <p className="text-xs text-[#737373] mb-6">{data.registration}</p>

        <div className="flex flex-col md:flex-row gap-6">
          {data.photo_url && (
            <img src={`${BACKEND_URL}${data.photo_url}`} alt={data.name} className="w-32 h-40 object-cover rounded-lg border border-[#eae5d9] mx-auto md:mx-0" />
          )}
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-wider text-[#737373] font-bold">Member ID</div>
            <div className="font-mono text-[#FF9933] text-lg font-bold">{data.member_id}</div>
            <div className="mt-3 text-[11px] uppercase tracking-wider text-[#737373] font-bold">Name</div>
            <div className="serif text-3xl text-[#000080] font-semibold">{data.name}</div>
            <div className="mt-3 grid grid-cols-2 gap-4">
              <div><div className="text-[11px] uppercase tracking-wider text-[#737373] font-bold">Designation</div><div className="font-semibold text-[#1A1A1A]">{data.designation}</div></div>
              <div><div className="text-[11px] uppercase tracking-wider text-[#737373] font-bold">Joining Date</div><div className="font-semibold text-[#1A1A1A]">{data.joining_date}</div></div>
              <div><div className="text-[11px] uppercase tracking-wider text-[#737373] font-bold">Status</div><div className="font-semibold uppercase" style={{ color: data.status === "active" ? "#138808" : "#737373" }}>{data.status}</div></div>
            </div>
          </div>
        </div>

        {data.matched_certificate && (
          <div className="mt-8 p-5 rounded-xl bg-[#138808]/5 border border-[#138808]/30" data-testid="matched-cert">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-5 h-5 text-[#138808]" />
              <div className="font-bold text-[#138808] text-sm uppercase tracking-wider">Certificate Verified</div>
            </div>
            <div className="text-sm text-[#1A1A1A]">Certificate No: <span className="font-mono font-bold">{data.matched_certificate.cert_no}</span></div>
            <div className="text-sm text-[#1A1A1A]">Type: {(data.matched_certificate.cert_type || "").replace("_", " ").toUpperCase()}</div>
            <div className="text-sm text-[#1A1A1A]">Issued: {new Date(data.matched_certificate.issued_at).toLocaleDateString("en-IN")}</div>
          </div>
        )}

        {data.all_certificates?.length > 0 && (
          <div className="mt-6">
            <div className="text-[11px] uppercase tracking-wider text-[#737373] font-bold mb-2">All Certificates ({data.all_certificates.length})</div>
            <div className="space-y-2">
              {data.all_certificates.map(c => (
                <div key={c.cert_no} className="flex items-center justify-between p-3 rounded-lg bg-[#F3EFE7] text-sm">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#FF9933]" />
                    <span className="font-mono font-semibold text-[#000080]">{c.cert_no}</span>
                    <span className="text-[#737373]">· {(c.cert_type || "").replace("_", " ")}</span>
                  </div>
                  <div className="text-xs text-[#737373]">{new Date(c.issued_at).toLocaleDateString("en-IN")}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 text-center text-xs text-[#737373]">
        This verification is provided by <Link to="/" className="text-[#000080] font-semibold">AHCCT</Link> · Powered by tamper-proof QR ledger
      </div>
    </div>
  );
}
